import { useReactiveVar } from '@apollo/client'
import {
    ElementType,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import React, { ComponentType, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTreeViewStyles } from '../../styles/useTreeViewStyles'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import { DragIndicator } from '@material-ui/icons'
import clsx from 'clsx'
import { useTreeHandlers } from 'components/WorkspaceEditor/hooks'
import { useEditingPermissions } from 'components/WorkspaceEditor/hooks/useEditingPermissions'
import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import { useShallowHoveredItems } from 'components/WorkspaceEditor/HoveredItemStore'
import { DataNode } from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useActivationDeactivationController } from '../NodeContents/useActivationDeactivationController'
import { withLazyLoad } from '../withLazyComponent'
import { AddIconProps } from './AddIcon'
import { useIsNodeBeingAdded } from './hooks/useIsNodeBeingAdded'
import { getCycledIndex } from './hooks/useMenuKeyboardNav'
import { SwitcherIconProps } from './SwitcherIcon'
import { VisibilityIconProps } from './VisibilityIcon'

/** Props for rendering visibility icon of <TreeView /> component */
export interface TreeViewHoveredIconsProps {
    /** The node to render */
    node: DataNode
    /** Classes to style the components */
    classes: ReturnType<typeof useTreeViewStyles>
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

/**
 * Renders the visibility icon property of <TreeView /> component
 * @param {VisibilityIconProps} props - Props for rendering visibility icon
 * @returns {ReactElement} Node visibility icon
 */
const LazyAddIcon = withLazyLoad<AddIconProps>(() =>
    import('./AddIcon').then((module) => ({
        default: module.AddIcon as ComponentType<any>,
    }))
)

const LazyVisibilityIcon = withLazyLoad<VisibilityIconProps>(() =>
    import('./VisibilityIcon').then((module) => ({
        default: module.VisibilityIcon as ComponentType<any>,
    }))
)

const LazySwitcherIcon = withLazyLoad<SwitcherIconProps>(() =>
    import('./SwitcherIcon').then((module) => ({
        default: module.SwitcherIcon as ComponentType<any>,
    }))
)

interface styleProps {
    iconCount: number
}

const useStyles = makeStyles((theme: Theme) => ({
    hoveredIconWrapper: {
        position: 'absolute',
        left: ({ iconCount }: styleProps) => -theme.spacing(iconCount * 3),
        width: 'auto',
        alignItems: 'center',
    },
}))

export const TreeViewHoveredIcons = memo<TreeViewHoveredIconsProps>(
    ({ node, classes, treeRef }) => {
        const {
            isContentTeamFromStudioPage,
            articlesLoading,
            deactivationPendingIds,
            expandedKeys,
            setIsEditingPlainText,
        } = useShallowSelector((state) => ({
            isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
            articlesLoading: state.articlesLoading,
            deactivationPendingIds: state.deactivationPendingIds,
            expandedKeys: state.expandedKeys,
            setIsEditingPlainText: state.actions.setIsEditingPlainText,
        }))

        const {
            disableProductTypeInactivation,
        } = useActivationDeactivationController()

        const { isDragging } = useReorderItems()
        const { canEdit } = useEditingPermissions()
        // Use the extracted hook to check if the node is being added

        const isNodeBeingAdded = useIsNodeBeingAdded(node)

        const { previewFeatures } = usePreviewFeatures()
        const disablePTAutomation = !previewFeatures.hasPTAutomation

        const {
            isMainMenuDisplayed,
            selectedMainMenuNode,
            hoveredNode,
        } = useShallowHoveredItems((state) => ({
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            hoveredNode: state.hoveredNode,
        }))
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
        const showMainMenuIcons = useMemo(
            () => node?.key === selectedMainMenuNode?.id,
            [node, selectedMainMenuNode]
        )
        const isDeactivationPending = deactivationPendingIds.includes(
            node.key as string
        )
        // True while ANY node has a pending mutation — disables all other eyeball icons.
        const isAnyMutationPending = deactivationPendingIds.length > 0

        const [isFocused, setIsFocused] = useState(false)

        const isIconVisible = useMemo(
            () =>
                isFocused ||
                (isMainMenuDisplayed
                    ? showMainMenuIcons
                    : node?.key === (hoveredNode as DataNode)?.key ||
                    isDeactivationPending),
            [
                node,
                hoveredNode,
                showMainMenuIcons,
                isMainMenuDisplayed,
                isDeactivationPending,
                isFocused,
            ]
        )

        const { selectedElementNode } = useReactiveVar(workspaceSelectedElement)
        const isVisibilityIconVisible = useMemo(
            () =>
                !isContentTeamFromStudioPage ||
                (isCurrentlyOnOMWorkspace &&
                    selectedElementNode?.elementType ===
                    ElementType.PRODUCTFAMILY) ||
                (isCurrentlyOnOMWorkspace &&
                    selectedElementNode?.elementType ===
                    ElementType.PRODUCTTYPE &&
                    !(node.contentItemView?.isForProductType === true)),
            [
                isContentTeamFromStudioPage,
                isCurrentlyOnOMWorkspace,
                selectedElementNode?.elementType,
                node.contentItemView?.isForProductType,
            ]
        )

        const isLoading = useMemo(
            () => articlesLoading.includes(node.key) || isDeactivationPending,
            [articlesLoading, node.key, isDeactivationPending]
        )

        useEffect(() => {
            // When loading starts, the focused child (e.g. VisibilityIcon SVG) unmounts.
            // React skips onBlur for unmounted elements, so isFocused never resets naturally.
            if (isLoading) {
                setIsFocused(false)
            }
        }, [isLoading])

        const DragTooltipContent = () => (
            <>
                <Typography className={classes.tooltipTitle}>
                    Click to open
                </Typography>
                <Typography className={classes.tooltipSubtitle}>
                    (Hold to drag)
                </Typography>
            </>
        )

        const {
            handleDragIconClick,
            handleMouseHover,
            handleMouseLeave,
            handleShowHideProperty,
        } = useTreeHandlers()

        const handleIconKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
            const isNav = e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Tab'
            if (!isNav) return
            const items = Array.from(
                e.currentTarget.querySelectorAll<HTMLElement>(
                    '[role="button"]:not([tabindex="-1"]), button:not([disabled])'
                )
            )
            if (!items.length) return
            const currentIdx = items.indexOf(e.target as HTMLElement)
            const goBack = e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)
            // For Tab: exit the toolbar at the boundary so focus moves to the next node
            if (e.key === 'Tab') {
                const atEnd = !goBack && currentIdx === items.length - 1
                const atStart = goBack && currentIdx === 0
                if (atEnd || atStart) return
            }
            e.preventDefault()
            items[getCycledIndex(currentIdx, items.length, goBack)].focus()
        }, [])

        // Toolbar roving tabindex: while collapsed only ONE icon per row stays
        // tabbable (drag, or AddIcon for tables) so focus can reveal the bar;
        // hidden icons get tabIndex=-1 + aria-hidden until reveal-on-focus.
        const isDragDisabled = !canEdit || isNodeBeingAdded

        const handleDragClick = (e: React.MouseEvent) => {
            if (!isDragDisabled) {
                handleDragIconClick(e, node)
            }
        }

        const handleDragKeyDown = (e: React.KeyboardEvent) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isDragDisabled) {
                e.preventDefault()
                handleDragIconClick(e, node)
            }
        }

        const handleDragMouseDown = !isDragDisabled
            ? () => setIsEditingPlainText(false)
            : undefined

        const dragIcon = (
            <DragIndicator
                fontSize="inherit"
                aria-label={
                    !isDragDisabled
                        ? 'Drag to reorder'
                        : isNodeBeingAdded
                            ? 'Drag disabled - content being added'
                            : 'Editing disabled - not in project'
                }
                role="button"
                tabIndex={isDragDisabled || !isIconVisible ? -1 : 0}
                onClick={handleDragClick}
                onKeyDown={handleDragKeyDown}
                onMouseDown={handleDragMouseDown}
                data-testid="drag-indicator"
                data-disabled={isDragDisabled}
                className={clsx(
                    classes.dragIndicator,
                    !isDragDisabled ? classes.enabledIcon : classes.disabledIcon
                )}
            />
        )

        const isArticle = useMemo(() => node.type === NODE_TYPES.Article, [
            node,
        ])
        // Tables are read-only/import-only - no drag-to-reorder affordance.
        const isTableNode = node.type === NODE_TYPES.Table
        const iconContainerClass = useMemo(
            () =>
                isArticle
                    ? clsx(classes.iconContainer, classes.iconContainerArticle)
                    : classes.iconContainer,
            [isArticle, classes]
        )
        const { hoveredIconWrapper } = useStyles({
            iconCount:
                (isArticle ? 4 : 3) -
                (!isVisibilityIconVisible ? 1 : 0) -
                (isTableNode ? 1 : 0),
        })
        return (
            <GridContainer
                onMouseOver={() => handleMouseHover(node)}
                onMouseLeave={handleMouseLeave}
                onFocus={() => setIsFocused(true)}
                onBlur={(e: React.FocusEvent) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setIsFocused(false)
                    }
                }}
                onKeyDown={handleIconKeyDown}
                className={hoveredIconWrapper}
                direction="row"
            >
                <GridItem
                    aria-hidden={!isIconVisible}
                    className={clsx(
                        iconContainerClass,
                        !isIconVisible && classes.hiddenHoveredIcon
                    )}
                >
                    <LazyAddIcon
                        node={node as DataNode}
                        isLoading={isLoading}
                    />
                </GridItem>
                {isVisibilityIconVisible && (
                    <GridItem
                        aria-hidden={!isIconVisible}
                        className={clsx(
                            iconContainerClass,
                            !isIconVisible && classes.hiddenHoveredIcon
                        )}
                    >
                        <LazyVisibilityIcon
                            contentTeamFromStudioPage={
                                isContentTeamFromStudioPage &&
                                !isCurrentlyOnOMWorkspace
                            }
                            isLoading={isLoading}
                            tabIndexOverride={
                                !isIconVisible ? (node.isHidden ? 0 : -1) : undefined
                            }
                            onClickIcon={(isHidden: boolean) =>
                                handleShowHideProperty(
                                    isHidden,
                                    node,
                                    disablePTAutomation
                                )
                            }
                            disabled={
                                ((disableProductTypeInactivation(node) &&
                                    !node.isProductGroup &&
                                    !node.isProductType) ??
                                    false) ||
                                isNodeBeingAdded ||
                                isAnyMutationPending
                            }
                            isHidden={node.isHidden ?? false}
                            isArticle
                        />
                    </GridItem>
                )}
                {!isTableNode && (
                    <GridItem
                        className={
                            !isIconVisible || isLoading
                                ? classes.hiddenHoveredIcon
                                : iconContainerClass
                        }
                    >
                        {!isDragging && (
                            <HoverTooltip
                                text={<DragTooltipContent />}
                                placements={['top', 'bottom']}
                            >
                                {dragIcon}
                            </HoverTooltip>
                        )}
                        {isDragging && dragIcon}
                    </GridItem>
                )}
                {isArticle && (
                    <LazySwitcherIcon
                        type={node.type}
                        isLoading={isLoading}
                        isExpanded={expandedKeys?.includes(node.key)}
                        isDisabled={isNodeBeingAdded}
                        node={node}
                        treeRef={treeRef}
                    />
                )}
            </GridContainer>
        )
    }
)
